import axios from "axios";
import { useEffect, useState } from "react";
import { configuration } from "src/configuration";
import { checkDisplayValue } from "src/utils";
import { constants } from "src/utils/const";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

const CitywiseClosingTable = ({ city_name, data, filteredCreditData, filteredDebitData }: any) => {
  const [creditData, setCreditData] = useState<any>([]);
  const [debitData, setDebitData] = useState<any>([]);
  const [creditLoading, setCreditLoading] = useState<any>(true);
  const [debitLoading, setDebitLoading] = useState<any>(true);
  const [creditOrder, setCreditOrder] = useState<any>("asc");
  const [creditSort, setCreditSort] = useState<any>("customer_code");
  const [debitOrder, setDebitOrder] = useState<any>("asc");
  const [debitSort, setDebitSort] = useState<any>("customer_code");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const processTableData = (data: any[], otherTableLength: number) => {
    if (!data || data.length === 0) {
      if (otherTableLength > 0) {
        return Array(otherTableLength).fill({
          code: '-',
          branchname: '-',
          point: '-',
          isDashRow: true
        });
      }
      return [];
    }

    const grandTotalRow = data.find(row => row.branchname === 'Grand Total');
    const regularRows = data.filter(row => row.branchname !== 'Grand Total');
    const otherTableRegularLength = otherTableLength > 0 ? otherTableLength - 1 : 0;
    const maxRows = Math.max(regularRows.length, otherTableRegularLength);
    const dashRowsNeeded = maxRows - regularRows.length;

    const dashRows = Array(Math.max(0, dashRowsNeeded)).fill({
      code: '-',
      branchname: '-',
      point: '-',
      isDashRow: true
    });

    let combinedRows = [...regularRows, ...dashRows];
    if (grandTotalRow) {
      combinedRows.push(grandTotalRow);
    }

    return combinedRows;
  };

  useEffect(() => {
    if (!creditLoading && !debitLoading) {
      const creditLength = creditData.length;
      const debitLength = debitData.length;

      const processedDebitData = processTableData(debitData, creditLength);
      const processedCreditData = processTableData(creditData, debitLength);

      setDebitData(processedDebitData);
      setCreditData(processedCreditData);
    }
  }, [creditLoading, debitLoading, creditData.length, debitData.length]);

  const getCreditReportData = async () => {
    try {
      setCreditLoading(true);
      let creditListData = await axios.get(`${configuration.apiUrl}/city/postFilterCityWiseClosingReport?perPage=100000&page=1&search=&city_id=${data.id}&sort=party_code&order=${creditOrder}&dr_cr=1`);
      creditListData = creditListData?.data?.responceData?.data ?? [];
      setCreditData(creditListData);
      setCreditLoading(false);
    } catch (err) {
      setCreditLoading(false);
      console.error("err", err);
    }
  };

  const getDebitReportData = async () => {
    try {
      setDebitLoading(true);
      let debitListData = await axios.get(`${configuration.apiUrl}/city/postFilterCityWiseClosingReport?perPage=100000&page=1&search=&city_id=${data.id}&sort=party_code&order=${debitOrder}&dr_cr=0`);
      debitListData = debitListData?.data?.responceData?.data ?? [];
      setDebitData(debitListData);
      setDebitLoading(false);
    } catch (err) {
      setDebitLoading(false);
      console.error("err", err);
    }
  };

  useEffect(() => {
    setCreditData(filteredCreditData);
  }, [filteredCreditData]);

  useEffect(() => {
    setDebitData(filteredDebitData);
  }, [filteredDebitData]);

  useEffect(() => {
    getDebitReportData();
    getCreditReportData();
  }, [data.id]);

  useEffect(() => {
    getDebitReportData();
  }, [debitOrder]);

  useEffect(() => {
    getCreditReportData();
  }, [creditOrder]);

  const handlePartyClick = async (partyCode: string) => {
    try {
      // Dispatch the party code to Redux store
      dispatch({ type: 'SET_RECEIVER_ID', payload: partyCode });
      
      // Navigate to party ledger page
      navigate('/report/party-ledger');
    } catch (error) {
      console.error("Error handling party click:", error);
    }
  };

  const renderBranchnameWithTooltip = (value: string, code: string) => {
    if (value === 'Grand Total') {
      return (
        <span className="party-name-cell">
          {checkDisplayValue(value)}
        </span>
      );
    }

    return (
      <OverlayTrigger
        key="top"
        placement="top"
        overlay={<Tooltip id={`tooltip-top`}>{checkDisplayValue(value)}</Tooltip>}
      >
        <span
          style={{ cursor: 'pointer', display: 'inline-block' }}
          className="party-name-cell"
          onClick={() => handlePartyClick(code)}
        >
          {checkDisplayValue(value) && checkDisplayValue(value).length > 20
            ? checkDisplayValue(value).substring(0, 20) + '...'
            : checkDisplayValue(value)}
        </span>
      </OverlayTrigger>
    );
  };

  const renderTableRow = (x: any, isDashRow = false) => {
    const rowStyle = {
      height: '45px',
      lineHeight: '25px',
      backgroundColor: x.branchname === 'Grand Total' ? '#f5f5f5' : undefined,
      color: 'black',
    };

    return (
      <tr style={rowStyle}>
        <td></td>
        <td>{isDashRow ? '-' : checkDisplayValue(x.code)}</td>
        <td style={{ fontWeight: x.branchname === 'Grand Total' ? 'bold' : 'normal' }}>
          {isDashRow ? '-' : renderBranchnameWithTooltip(x.branchname, x.code)}
        </td>
        <td style={{ fontWeight: x.branchname === 'Grand Total' ? 'bold' : 'normal' }}>
          {isDashRow ? '-' : constants.formatAmount(
            parseInt((x.point || '0').toString().replace(/,/g, '')) || 0
          )}
        </td>
      </tr>
    );
  };

  const getRecordCount = (data: any[]) => {
    if (!data || data.length === 0) return 0;
    return data.filter(row => row.branchname !== 'Grand Total' && !row.isDashRow).length;
  };

  // Helper to get grand total value from table data
  const getGrandTotal = (dataArr: any[]) => {
    const grandTotalRow = dataArr.find(row => row.branchname === 'Grand Total');
    return grandTotalRow ? parseInt((grandTotalRow.point || '0').toString().replace(/,/g, '')) || 0 : 0;
  };

  // Calculate grand totals
  const debitGrandTotal = getGrandTotal(debitData);
  const creditGrandTotal = getGrandTotal(creditData);
  
  // Calculate difference with adjusted debit
  const adjustedDebit = debitGrandTotal < 0 ? Math.abs(debitGrandTotal) : debitGrandTotal;
  const difference = creditGrandTotal - adjustedDebit;

  // Determine which side to show difference
  let showDebitDifference = false;
  let showCreditDifference = false;
  
  if (creditGrandTotal === 0 && debitGrandTotal !== 0) {
    showCreditDifference = true;
  } else if (debitGrandTotal === 0 && creditGrandTotal !== 0) {
    showDebitDifference = true;
  } else if (creditGrandTotal < adjustedDebit) {
    showCreditDifference = true;
  } else if (adjustedDebit < creditGrandTotal) {
    showDebitDifference = true;
  }

  // Helper to render Difference row
  const renderDifferenceRow = (isDebit: boolean) => {
    if (Math.abs(difference) === 0) return null;
    
    return (
      <tr style={{ backgroundColor: '#f9f9f9' }}>
        <td></td>
        <td></td>
        <td style={{ fontWeight: 'bold', color: '#181C32' }}>Difference</td>
        <td style={{ fontWeight: 'bold', color: '#181C32' }}>
          {isDebit 
            ? (showDebitDifference ? constants.formatAmount(Math.abs(difference)) : '-')
            : (showCreditDifference ? constants.formatAmount(Math.abs(difference)) : '-')
          }
        </td>
      </tr>
    );
  };

  // Helper to render table rows with Difference row above Grand Total
  const renderTableRowsWithDifference = (dataArr: any[], isDebit: boolean) => {
    const rows = [];
    for (let i = 0; i < dataArr.length; i++) {
      const row = dataArr[i];
      if (row.branchname === 'Grand Total') {
        // Insert Difference row before Grand Total
        const diffRow = renderDifferenceRow(isDebit);
        if (diffRow) rows.push(diffRow);
      }
      rows.push(renderTableRow(row, row.isDashRow));
    }
    return rows;
  };

    return (
        <div id="kt_app_content" className="app-content flex-column-fluid">
            <div id="kt_app_content_container">
                <div className="row">
                    <div className="col-md-6">
                        <div className="card">
                            <div className="card-header border-0 bg-gray-600 py-4 min-h-auto">
                                <div className="d-flex justify-content-between w-100">
                                    <h2 className="mb-0 fs-5"><br />Debit</h2>
                                    <h2 className="mb-0 fs-5"><br />{data?.city_name}</h2>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table id="kt_table_general_report_debit" 
                                           className="table align-middle table-row-dashed sentreceiveparts-report fs-6 dataTable no-footer" 
                                           style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                                        <thead>
                                            <tr className="text-start fw-bolder fs-6 text-uppercase" style={{ height: '20px', color: 'black' }}>
                                                <th colSpan={1} className="w-10px pe-2">
                                                    <div className="form-check form-check-sm form-check-custom form-check-solid me-3"></div>
                                                </th>
                                                <th colSpan={1} className={`min-w-125px table-sort-${debitOrder}`} 
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setDebitOrder(debitOrder === "asc" ? "desc" : "asc")}>
                                                    Code
                                                </th>
                                                <th colSpan={1} className="min-w-125px">Party</th>
                                                <th colSpan={1} className="min-w-125px">Point</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600">
                                            {debitLoading ? (
                                                <tr>
                                                    <td colSpan={4}>
                                                        <div className="d-flex text-center w-100 align-content-center justify-content-center">Loading...</div>
                                                    </td>
                                                </tr>
                                            ) : debitData.length > 0 ? (
                                                renderTableRowsWithDifference(debitData, true)
                                            ) : (
                                                <tr>
                                                    <td colSpan={4}>
                                                        <div className="d-flex text-center w-100 align-content-center justify-content-center">No matching records found</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex justify-content-end p-3">
                                    <span>
                                        Showing {getRecordCount(debitData)} records
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card">
                            <div className="card-header border-0 bg-gray-600 py-4 min-h-auto">
                                <div className="d-flex justify-content-between w-100">
                                    <h2 className="mb-0 fs-5"><br />Credit</h2>
                                    <h2 className="mb-0 fs-5"><br />{data?.city_name}</h2>
                                </div>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table id="kt_table_general_report_credit" 
                                           className="table align-middle table-row-dashed sentreceiveparts-report fs-6 dataTable no-footer" 
                                           style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                                        <thead>
                                            <tr className="text-start fw-bolder fs-6 text-uppercase" style={{ height: '20px' }}>
                                                <th colSpan={1} className="w-10px pe-2">
                                                    <div className="form-check form-check-sm form-check-custom form-check-solid me-3"></div>
                                                </th>
                                                <th colSpan={1} className={`min-w-125px table-sort-${creditOrder}`}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setCreditOrder(creditOrder === "asc" ? "desc" : "asc")}>
                                                    Code
                                                </th>
                                                <th colSpan={1} className="min-w-125px">Party</th>
                                                <th colSpan={1} className="min-w-125px">Point</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600">
                                            {creditLoading ? (
                                                <tr>
                                                    <td colSpan={4}>
                                                        <div className="d-flex text-center w-100 align-content-center justify-content-center">Loading...</div>
                                                    </td>
                                                </tr>
                                            ) : creditData.length > 0 ? (
                                                renderTableRowsWithDifference(creditData, false)
                                            ) : (
                                                <tr>
                                                    <td colSpan={4}>
                                                        <div className="d-flex text-center w-100 align-content-center justify-content-center">No matching records found</div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex justify-content-end p-3">
                                    <span>
                                        Showing {getRecordCount(creditData)} records
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CitywiseClosingTable;
